/**
 * When a referee slot opens — the two league-time window predicates, and the
 * single rule that derives `referee_slots.opened_at` from them.
 *
 * ⚠️ THESE LIVE HERE BECAUSE TWO WORKERS DECIDE THE SAME THING. The referee
 * Worker's deadline crons open slots on a schedule; the scheduler Worker's
 * `regenerateRefereeSlots` resets and creates slots at arbitrary times, and it
 * has to reach the same answer or a regenerated slot sits closed until the next
 * cron fire. Before 2026-09-07 only the crons knew the rule, and a regen on a
 * Friday made that weekend's touched slots unclaimable until Sunday.
 *
 * Both predicates compare WALL-CLOCK representations in the league zone, never
 * ms arithmetic, and both step back to a WEEKDAY rather than by a fixed number
 * of days (see `mostRecentWeekdayBefore` for the bug that forced that). Both
 * deadlines (Thu 12:00, Sun 00:00) sit far from the 02:00 DST transition, so the
 * comparison is monotonic and DST-correct by construction. See docs/timezones.md.
 */

import { designationAge } from './divisions'
import { LEAGUE_TZ, leagueParts, leagueToday } from './leagueTime'

const TZ = LEAGUE_TZ

/** ⚠️ A NAMED PASSTHROUGH over `leagueTime.ts`. The rule is shared with the
 *  field-duty send and the refresh cron; this name keeps the claim-window code
 *  below reading in its own vocabulary. It must never grow a rule of its own. */
const tzParts = leagueParts

/** Calendar day step on a YYYY-MM-DD string. Uses UTC (no DST) so it is pure
 *  calendar math — the DST-safe `setDate`-style pattern from docs/timezones.md. */
function addDaysISO(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Weekday index (0 = Sunday … 6 = Saturday) for a YYYY-MM-DD. Parsed as UTC so
 *  it is pure calendar math with no DST in it, the same reason `addDaysISO`
 *  uses UTC. Exported for the field-duty send day. */
export function weekdayOf(iso: string): number {
  return new Date(`${iso}T00:00:00Z`).getUTCDay()
}

// Exported alongside `mostRecentWeekdayBefore` so a caller needing "the
// Sunday before" (e.g. team.tsx's claim-window display) does not have to
// spell out the JS weekday index itself.
export const SUNDAY = 0
const THURSDAY = 4

/** The most recent `targetWeekday` STRICTLY BEFORE `iso`, as YYYY-MM-DD.
 *
 *  ⚠️ THIS REPLACES TWO FIXED OFFSETS THAT WERE ONLY CORRECT FOR SATURDAY
 *  GAMES. `isRegionWindowOpen` subtracted 6 and `isPastThursdayNoon`
 *  subtracted 2; off a SUNDAY game those land on a Monday and a Friday, the
 *  wrong weekday entirely. Region 13 plays Saturday and Sunday, and the two are
 *  ONE week (owner, 2026-09-08), so a Sunday game's boundaries are its week's:
 *  one day further back than a Saturday's.
 *
 *  "Strictly before" is what makes that come out right — from a Sunday game the
 *  previous Sunday is 7 days back, never 0. For a Saturday game the result is
 *  identical to the old constants (6 and 2), which is why this is safe to land
 *  on a live season: 1,734 of the season's 1,735 games are Saturdays.
 *
 *  Exported (2026-09-08): `apps/referee-spa/src/routes/team.tsx` had grown its
 *  own `sundayOf`, reimplementing exactly the fixed-offset bug this function
 *  was written to fix. Reimplementing an offset is what this comment used to
 *  warn against — export it here instead. */
export function mostRecentWeekdayBefore(iso: string, targetWeekday: number): string {
  const back = ((weekdayOf(iso) - targetWeekday + 6) % 7) + 1
  return addDaysISO(iso, -back)
}

/** True once `nowMs` is at/after 12:00 noon league-time on the Thursday of the
 *  game's week. After this the responsible team's exclusive window closes and
 *  the slot opens to any eligible referee. */
export function isPastThursdayNoon(nowMs: number, gameDateISO: string, tz = TZ): boolean {
  const thursday = mostRecentWeekdayBefore(gameDateISO, THURSDAY)
  const { date, hour } = tzParts(nowMs, tz)
  if (date > thursday) return true
  return date === thursday && hour >= 12
}

/** True once `nowMs` is at/after 00:00 league-time on the Sunday that begins the
 *  game's week. This is the FLOOR under every slot of either class: before it,
 *  nobody may claim — not even the responsible team (owner, 2026-09-08; see
 *  `windowGate`, which is where the team side of that is enforced).
 *
 *  Exactly midnight, deliberately: the Sunday openers fire at 00:00 league-time
 *  (`0 7 * * SUN` PDT / `0 8 * * SUN` PST) and a `>=` comparison includes their
 *  instant. A 00:01 threshold would make that fire find nothing due, and the
 *  next fire is Thursday 19:00 — Region slots would open four days late.
 *
 *  Renamed from `isRegionWindowOpen` on 2026-09-08: it governs both classes
 *  now, so a Region-only name would misdescribe it. */
export function isWeekWindowOpen(nowMs: number, gameDateISO: string, tz = TZ): boolean {
  const sunday = mostRecentWeekdayBefore(gameDateISO, SUNDAY)
  return tzParts(nowMs, tz).date >= sunday // Sunday 00:00 = first instant of that league day
}

/** `isWeekWindowOpen` for a slot whose game may have no date. An UNDATED game
 *  is CLOSED (owner, 2026-10-09): it has no week to open. */
export function isGameWeekOpen(nowMs: number, gameDateISO: string | null): boolean {
  return !!gameDateISO && isWeekWindowOpen(nowMs, gameDateISO)
}

/** Is a team-duty slot inside its claim window right now? A ref-admin
 *  force-open (`opened_at`) wins; otherwise its game's week must have opened.
 *
 *  ⚠️ ONE RULE FOR THE SERVER AND THE BUTTON. `planWindow` branches on the same
 *  two facts (it reports which one admitted the claim), and the My teams card
 *  decides whether to render Claim from this. Until 2026-10-09 the card treated
 *  an undated slot as open, so it offered a button the server always refused.
 *  Region slots add their own rule on top (`planWindow`). */
export function isClaimWindowOpen(nowMs: number, gameDateISO: string | null, openedAt: string | null): boolean {
  return !!openedAt || isGameWeekOpen(nowMs, gameDateISO)
}

/** `isPastThursdayNoon` or `isWeekWindowOpen`, as a value. */
export type WindowPredicate = (nowMs: number, gameDateISO: string, tz?: string) => boolean

/** The LATEST game date whose `predicate` window has already opened at `nowMs`,
 *  as YYYY-MM-DD — or null when none has. `date <= result` is then an exact
 *  restatement of the predicate, usable as a SQL bound.
 *
 *  ⚠️ THIS EXISTS SO A QUERY CAN BOUND ITSELF WITHOUT RESTATING THE RULE IN SQL.
 *  `openSlots` used to read every never-opened slot in the season and filter row
 *  by row in TypeScript, then write the surviving ids back as an `id IN (?,?,…)`
 *  list — which is what blew D1's 100-bound-param cap on 2026-09-10 and closed
 *  the referee board for a game weekend. One cutoff date makes both halves
 *  parameter-count-constant at any season size.
 *
 *  **Why a single cutoff is exact, not an approximation.** Both predicates are
 *  monotonic in the game date for a fixed `nowMs`: `mostRecentWeekdayBefore` is
 *  non-decreasing in `gameDateISO`, and "has `nowMs` passed that boundary" is
 *  decreasing in the boundary, so the composition only ever steps from true to
 *  false, exactly once. Past-dated games stay included, which is deliberate — an
 *  unopened slot on a game already played is still due.
 *
 *  **Why it asks the predicate instead of computing the date.** The weekday and
 *  DST arithmetic stays in exactly one place, the place this file's header
 *  insists on. A closed form would be a second copy of the rule wearing a
 *  different shape, and the fixed-offset bug in `mostRecentWeekdayBefore`'s
 *  docblock is what that costs.
 *
 *  **Why 8 days is a sufficient bound.** `mostRecentWeekdayBefore` steps back
 *  1-7 days, so a game's boundary is never earlier than `gameDate - 7`; a
 *  boundary at or before `nowMs` therefore forces `gameDate <= today + 7`. The
 *  loop cannot terminate early and miss a later true date, because the predicate
 *  is monotonic — the first false is the end. */
export function latestOpenGameDate(
  nowMs: number,
  predicate: WindowPredicate,
  tz = TZ,
): string | null {
  const today = tzParts(nowMs, tz).date
  if (!predicate(nowMs, today, tz)) return null
  let cutoff = today
  for (let ahead = 1; ahead <= 8; ahead++) {
    const candidate = addDaysISO(today, ahead)
    if (!predicate(nowMs, candidate, tz)) break
    cutoff = candidate
  }
  return cutoff
}

/** The `Region` sentinel stored in `referee_slots.responsibility`. Mirrors
 *  `REGION` in src/scheduling/referee_duty.py, which is what writes it. */
export const REGION_RESPONSIBILITY = 'Region'

/**
 * Has this slot's window already opened at `nowMs`?
 *
 * This answers "should `opened_at` be set" — i.e. "is this slot open to
 * EVERYONE" — not "is anyone able to claim it". The two classes reach that
 * state on different clocks: a **Region** slot is open to all eligible referees
 * from its week's Sunday, while a **team** slot is claimable by its own team
 * from that same Sunday (a floor `windowGate` enforces, not this function) and
 * opens to everyone at Thursday noon.
 *
 * A slot whose game has no date cannot be windowed at all, and answers false.
 */
export function slotWindowIsOpen(
  nowMs: number,
  gameDateISO: string | null,
  responsibility: string,
  tz = TZ,
): boolean {
  if (!gameDateISO) return false
  return responsibility === REGION_RESPONSIBILITY
    ? isWeekWindowOpen(nowMs, gameDateISO, tz)
    : isPastThursdayNoon(nowMs, gameDateISO, tz)
}

/** Alias of `designationAge`, kept because the referee Worker imports it by
 *  this name. PURE. Age parsed from a division NAME's leading number ("B10" → 10,
 *  "G6" → 6, "6U" → 6). Fallback for when `divisions.age` was not backfilled.
 *  Returns null for a malformed or empty name.
 *
 *  Moved here from `apps/referee/src/dao/refereeSlots.ts` on 2026-09-08: the
 *  scheduler Worker, the referee Worker and the SPA all need the young/not-young
 *  answer now, and three copies is how a rule drifts. */
export function ageFromDivisionName(name: string): number | null {
  return designationAge(name)
}

/** PURE. Is this a "young" division — 6U/7U/8U, where parents officiate their
 *  own game and every slot reads plain "Referee"?
 *
 *  Prefers the stored `divisions.age`; falls back to parsing the division name.
 *  A division whose age cannot be resolved at all answers **false** — i.e. it
 *  gets the competitive UI, matching the referee product's "when in doubt, show
 *  the controls" bias. `positionLabelFor` depends on that default agreeing.
 *
 *  ⚠️ True for 4U/5U as well, which is harmless: young slot derivation is driven
 *  by `games` rows and those divisions play none. Do not "fix" this into a
 *  6-to-8 range check without checking who else calls it. */
export function isYoungDivision(division: string, age: number | null): boolean {
  const resolved = age ?? ageFromDivisionName(division)
  return resolved !== null && resolved <= 8
}

/** PURE. A 6U, 7U or 8U division, whose games have no card and no score.
 *  ⚠️ Not `isYoungDivision`, which also takes 4U and 5U; those are the Preschool
 *  planner's. So a forfeit here is a status and a notice, never a result. */
export function playsWithoutScore(division: string): boolean {
  const age = ageFromDivisionName(division)
  return age !== null && age >= 6 && age <= 8
}

/** A young (6U/7U/8U) game can be played once this many of its slots are filled
 *  (owner, 2026-09-26). Lives here, not in `gameCoverage.ts`, because
 *  `slotIsOptional` needs it and `gameCoverage.ts` imports this file; it is
 *  re-exported from there so existing imports keep working. */
export const YOUNG_GAME_MIN_FILLED = 2

/** PURE. Does this slot read "(optional)"? Owner, 2026-10-09: it follows the
 *  coverage rule (`gameCoverage`), not the position. At 6U/7U/8U a game with two
 *  officials can be played, so once two slots are filled every still-open slot
 *  is optional, CR included. A filled slot never carries the marker.
 *
 *  ⚠️ A DISPLAY RULE SHARED WITH TEMPLATES: it decides the email suffix, the
 *  Thursday nudge and Follow-up's "missed". Change it with all three in view. */
export function slotIsOptional(ageGroup: string, slotFilled: boolean, gameFilled: number): boolean {
  return isYoungDivision(ageGroup, null) && !slotFilled && gameFilled >= YOUNG_GAME_MIN_FILLED
}

// ── Mentor board bounds ─────────────────────────────────────────────────────
//
// ⚠️ DAY GRANULARITY, DELIBERATELY, AND THIS WAS DECIDED TWICE. The first ask
// (owner, 2026-09-15) was that a mentor opening disappear "through the time
// that particular game ends", which would have meant a per-game end instant
// computed from the FIELD WINDOW. It was changed the same day to end-of-day, to
// match the referee open board: `openBoardFloor` in
// apps/referee/src/dao/refereeSlots.ts holds a game on the board until league
// midnight, for the reason written there — a board that dropped a game the
// minute it kicked off would also drop one running late. Two boards on the same
// site disagreeing about when a Saturday game stops mattering is the drift this
// file exists to prevent, so do not reintroduce a game-end instant here without
// changing the open board too.
//
// Both bounds are plain YYYY-MM-DD strings so a caller can hand them straight
// to SQL as two bound parameters, which is what keeps the mentor queries
// parameter-count-constant at any season size — see `latestOpenGameDate`'s
// docblock for the D1 cap incident that rule comes from.

/** PURE. Is this game's day already over in league time?
 *
 *  The floor the mentor surfaces use to drop what has been played, and the one
 *  the referee push-pending pass uses to stop pushing a held slot to the
 *  registrar once its game is over. League midnight rather than UTC midnight:
 *  17:00 Pacific is already tomorrow in UTC, so a UTC comparison calls a
 *  Saturday game past while Saturday afternoon is still running.
 *
 *  A game with no date answers **false** — there is no day to have ended, and
 *  hiding an undated game would be a different rule wearing this one's name. */
export function gameIsPast(nowMs: number, gameDateISO: string | null, tz = TZ): boolean {
  return gameDateISO !== null && gameDateISO < leagueToday(nowMs, tz)
}

/** The mentor surfaces' name for `gameIsPast`, kept so their call sites and
 *  the comments that cite it still read. Same function, not a copy. */
export const mentorGameIsPast = gameIsPast

/** PURE. The INCLUSIVE game-date range the mentor board shows, as YYYY-MM-DD:
 *  today in league time through the last date whose week has already begun.
 *
 *  `to` is `isWeekWindowOpen` restated as a bound, so the range covers the
 *  current week's Saturday AND Sunday — Region 13 plays both and the two are
 *  one week (owner, 2026-09-08). It steps forward a whole week in one move at
 *  Sunday 00:00, which is the same instant the Sunday slot openers fire on.
 *
 *  `from` is the negation of `gameIsPast`, spelled once here so the two
 *  cannot disagree about where midnight is.
 *
 *  The `?? from` is unreachable: `latestOpenGameDate` returns null only when
 *  the predicate is false for today itself, and today's own week always began
 *  1-7 days ago. It is written rather than asserted because an inverted range
 *  is the failure this would produce, and `from` makes it empty instead. */
export function mentorBoardBounds(nowMs: number, tz = TZ): { from: string; to: string } {
  const from = leagueToday(nowMs, tz)
  return { from, to: latestOpenGameDate(nowMs, isWeekWindowOpen, tz) ?? from }
}
